var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useCallback, useMemo } from 'react';
import { trpc } from 'root/src/client/v2/trpc/trpc';
const STALE_TIME_MS = 1000 * 10;
// A creator's delivered dares / clips for the redesign profile (creators.getCreatorProjects wraps
// the legacy GET_PROJECTS creatorProjects section). `subSection` is one of all | completed | active.
// Server-paginated via infinite scroll: each page returns a `nextCursor` (next page number) until
// the last page. Pages are flattened into a single `clips` list.
export const useCreatorClips = (recordId, subSection) => {
    var _a;
    const query = trpc.creators.getCreatorProjects.useInfiniteQuery({ recordId: recordId !== null && recordId !== void 0 ? recordId : '', subSection }, {
        enabled: Boolean(recordId),
        staleTime: STALE_TIME_MS,
        retry: false,
        initialCursor: 1,
        getNextPageParam: lastPage => lastPage.nextCursor,
    });
    const clips = useMemo(() => { var _a, _b; return ((_b = (_a = query.data) === null || _a === void 0 ? void 0 : _a.pages.flatMap(page => { var _a; return (_a = page.items) !== null && _a !== void 0 ? _a : []; })) !== null && _b !== void 0 ? _b : []); }, [(_a = query.data) === null || _a === void 0 ? void 0 : _a.pages]);
    return {
        clips,
        isLoading: query.isLoading,
        isError: query.isError,
        refetch: query.refetch,
        hasNextPage: query.hasNextPage,
        fetchNextPage: query.fetchNextPage,
        isFetchingNextPage: query.isFetchingNextPage,
    };
};
export const CLIP_SUBSECTIONS = ['all', 'completed', 'active'];
const totalOf = (data) => data === null || data === void 0 ? void 0 : data.total;
// Per-tab totals for the profile's dare-clips tabs. Reuses getCreatorProjects, which already returns a
// `total` from its own COUNT(*), with pageItemLength: 1 so each call fetches the count without pulling
// a full page of items. Counts stay undefined until their query resolves (so the tab pills only appear
// once there is a real number to show).
export const useCreatorClipCounts = (recordId) => {
    const enabled = Boolean(recordId);
    const options = { enabled, staleTime: STALE_TIME_MS, retry: false };
    const all = trpc.creators.getCreatorProjects.useQuery({ recordId: recordId !== null && recordId !== void 0 ? recordId : '', subSection: 'all', pageItemLength: 1 }, options);
    const completed = trpc.creators.getCreatorProjects.useQuery({ recordId: recordId !== null && recordId !== void 0 ? recordId : '', subSection: 'completed', pageItemLength: 1 }, options);
    const active = trpc.creators.getCreatorProjects.useQuery({ recordId: recordId !== null && recordId !== void 0 ? recordId : '', subSection: 'active', pageItemLength: 1 }, options);
    const counts = useMemo(() => ({
        all: totalOf(all.data),
        completed: totalOf(completed.data),
        active: totalOf(active.data),
    }), [all.data, completed.data, active.data]);
    // Kept referentially stable: it is threaded down to every rendered dare card as `onChanged`, where a
    // new identity on each render would re-render the whole list.
    const refetchAll = all.refetch;
    const refetchCompleted = completed.refetch;
    const refetchActive = active.refetch;
    const refetch = useCallback(() => __awaiter(void 0, void 0, void 0, function* () { yield Promise.all([refetchAll(), refetchCompleted(), refetchActive()]); }), [refetchAll, refetchCompleted, refetchActive]);
    return { counts, refetch };
};
